import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Send, ImagePlus, Trash2, Smile, Loader2, MessageSquare, Sticker } from "lucide-react";
import GifPicker from "@/components/GifPicker";

const timeShort = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/** Per-challenge trash talk: messages, reactions, @mentions and image posting. */
export default function ChallengeChat({ contestId, members = [], managerId, me }) {
  const [messages, setMessages] = useState([]);
  const [palette, setPalette] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingGif, setPendingGif] = useState(null);
  const [gifOpen, setGifOpen] = useState(false);
  const [reactFor, setReactFor] = useState(null);
  const bottom = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/chat/${contestId}`);
      setMessages(data.messages);
      setPalette(data.reactions);
    } catch (e) { /* keeps last state */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [contestId]);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const mentionedIds = (text) =>
    members
      .filter((m) => m.user_id !== me?.user_id)
      .filter((m) => text.toLowerCase().includes(`@${(m.name || "").split(" ")[0].toLowerCase()}`))
      .map((m) => m.user_id);

  const send = async (gif = pendingGif) => {
    const text = body.trim();
    if (!text && !pendingImage && !gif) return;
    setSending(true);
    try {
      await api.post(`/chat/${contestId}`, {
        body: text,
        image_id: pendingImage?.image_id || null,
        gif_url: gif?.gif_url || null,
        gif_id: gif?.gif_id || null,
        mentions: mentionedIds(text),
      });
      setBody("");
      setPendingImage(null);
      setPendingGif(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message failed");
    } finally { setSending(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/chat/${contestId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingImage(data);
      toast.success("Image ready — hit send");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const react = async (messageId, emoji) => {
    setReactFor(null);
    try {
      const { data } = await api.post(`/chat/${contestId}/${messageId}/react`, { emoji });
      setMessages((prev) => prev.map((m) => (m.message_id === messageId ? { ...m, reactions: data.reactions } : m)));
    } catch (e) {
      toast.error("Reaction failed");
    }
  };

  const remove = async (messageId) => {
    try {
      await api.delete(`/chat/${contestId}/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.message_id !== messageId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const insertMention = (name) => {
    setBody((b) => `${b}${b.endsWith(" ") || !b ? "" : " "}@${name.split(" ")[0]} `);
    setMentionOpen(false);
  };

  const renderBody = (text) =>
    (text || "").split(/(@\w+)/g).map((chunk, i) =>
      chunk.startsWith("@") ? (
        <span key={i} className="text-[#00FF66] font-semibold">{chunk}</span>
      ) : (
        <span key={i}>{chunk}</span>
      ),
    );

  return (
    <div className="border border-white/10 bg-[#141414] flex flex-col" data-testid="challenge-chat">
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <div className="font-display text-xl inline-flex items-center gap-2">
          <MessageSquare size={16} className="text-[#00FF66]" /> TRASH TALK
        </div>
        <span className="text-white/30 text-[10px] uppercase tracking-widest">{messages.length} msgs</span>
      </div>

      <div className="max-h-[380px] min-h-[180px] overflow-y-auto p-3 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-white/40 text-sm text-center py-8" data-testid="chat-empty">
            Nothing said yet. Fire the first shot.
          </div>
        )}
        {messages.map((m) => {
          const mine = m.user_id === me?.user_id;
          const canDelete = mine || managerId === me?.user_id;
          return (
            <div key={m.message_id} className="group" data-testid={`chat-msg-${m.message_id}`}>
              <div className="flex items-baseline gap-2">
                <span className={`text-xs font-bold ${mine ? "text-[#00FF66]" : "text-white/80"}`}>
                  {mine ? "You" : m.user_name}
                </span>
                <span className="text-white/25 text-[10px]">{timeShort(m.created_at)}</span>
                <button
                  onClick={() => setReactFor(reactFor === m.message_id ? null : m.message_id)}
                  data-testid={`chat-react-btn-${m.message_id}`}
                  className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-white transition-opacity"
                >
                  <Smile size={13} />
                </button>
                {canDelete && (
                  <button
                    onClick={() => remove(m.message_id)}
                    data-testid={`chat-delete-btn-${m.message_id}`}
                    className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-[#FF3B30] transition-opacity"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              {m.body && <div className="text-sm text-white/80 break-words">{renderBody(m.body)}</div>}
              {m.gif_url && (
                <img
                  src={m.gif_url}
                  alt={"GIF via GIPHY"}
                  data-testid={`chat-gif-${m.message_id}`}
                  className="mt-1 max-h-52 rounded-sm border border-white/10"
                />
              )}
              {m.image_url && (
                <img
                  src={m.image_url}
                  alt="shared"
                  data-testid={`chat-image-${m.message_id}`}
                  className="mt-1 max-h-52 rounded-sm border border-white/10"
                />
              )}
              {reactFor === m.message_id && (
                <div className="mt-1 flex gap-1" data-testid={`chat-react-palette-${m.message_id}`}>
                  {palette.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => react(m.message_id, emoji)}
                      className="px-1.5 py-0.5 border border-white/10 hover:border-[#00FF66] rounded-sm text-sm"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              {Object.keys(m.reactions || {}).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(m.reactions).map(([emoji, users]) => (
                    <button
                      key={emoji}
                      onClick={() => react(m.message_id, emoji)}
                      data-testid={`chat-reaction-${m.message_id}-${emoji}`}
                      className={`px-1.5 py-0.5 text-[11px] rounded-full border ${
                        users.includes(me?.user_id)
                          ? "border-[#00FF66]/60 bg-[#00FF66]/10 text-[#00FF66]"
                          : "border-white/10 text-white/60"
                      }`}
                    >
                      {emoji} {users.length}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottom} />
      </div>

      {gifOpen && (
        <GifPicker
          onClose={() => setGifOpen(false)}
          onPick={(gif) => { setGifOpen(false); send(gif); }}
        />
      )}

      {pendingImage && (
        <div className="px-3 py-2 border-t border-white/10 flex items-center justify-between text-xs text-white/60"
          data-testid="chat-pending-image">
          <span>Image attached</span>
          <button onClick={() => setPendingImage(null)} className="text-white/40 hover:text-[#FF3B30]">remove</button>
        </div>
      )}

      <div className="p-3 border-t border-white/10">
        {mentionOpen && (
          <div className="mb-2 flex flex-wrap gap-1" data-testid="chat-mention-list">
            {members.filter((m) => m.user_id !== me?.user_id).map((m) => (
              <button
                key={m.user_id}
                onClick={() => insertMention(m.name || "friend")}
                className="px-2 py-1 text-[11px] border border-white/15 hover:border-[#00FF66] rounded-sm text-white/70"
              >
                @{(m.name || "friend").split(" ")[0]}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMentionOpen((v) => !v)}
            data-testid="chat-mention-btn"
            className="px-2 py-2 text-white/50 hover:text-white border border-white/10 rounded-sm text-sm font-bold"
            title="Mention a friend"
          >
            @
          </button>
          <button
            onClick={() => setGifOpen((v) => !v)}
            data-testid="chat-gif-btn"
            className={`px-2 py-2 border border-white/10 rounded-sm ${gifOpen ? "text-[#00FF66] border-[#00FF66]/50" : "text-white/50 hover:text-white"}`}
            title="Search GIFs"
          >
            <Sticker size={15} />
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="chat-image-btn"
            className="px-2 py-2 text-white/50 hover:text-white border border-white/10 rounded-sm"
            title="Post an image or GIF"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            data-testid="chat-file-input"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Talk your talk..."
            maxLength={500}
            data-testid="chat-input"
            className="flex-1 bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2 outline-none text-sm"
          />
          <button
            onClick={() => send()}
            disabled={sending || (!body.trim() && !pendingImage)}
            data-testid="chat-send-btn"
            className="px-3 py-2 bg-[#00FF66] hover:bg-[#00e65b] disabled:opacity-40 text-black rounded-sm"
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
